const CURRENT_VERSION = 'v1.0';
const PROVIDER_NAME = 'BCM INTERMEDIACIONES DIGITALES';
const PROVIDER_LEGAL_NAME = 'BCM INTERMEDIACIONES DIGITALES SpA';
const SOFTWARE_NAME = 'Paper Boat';
const WEBSITE = 'www.paper-boat.cl';

const DOC_POINTS = [
  'Mantener estricta confidencialidad sobre toda información sensible, comercial, financiera y de cualquier otra naturaleza a la que acceda mediante el sistema, tanto durante como después de mi relación laboral.',
  'No copiar, reproducir, transferir, compartir, publicar ni divulgar datos del sistema a terceros no autorizados, por ningún medio físico o digital.',
  'Utilizar mis credenciales de acceso (usuario y contraseña) exclusivamente para fines laborales autorizados por mi empleador, sin compartirlas con otras personas.',
  'Reportar de inmediato cualquier incidente de seguridad, acceso no autorizado, pérdida de credenciales o vulnerabilidad que detecte en el sistema.',
  'Comprender y aceptar que el incumplimiento de este compromiso puede derivar en responsabilidad civil y/o penal según la legislación chilena vigente, incluyendo la Ley N° 19.628 y demás normas aplicables.'
];

async function loadImageAsBase64(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

export async function generateConfidentialityPDF({ userName, userEmail, acceptedAt, ipAddress, version, returnBase64 = false }) {
  const { jsPDF } = await import('jspdf');
  const doc = new jsPDF();
  const pw = doc.internal.pageSize.getWidth();
  const ph = doc.internal.pageSize.getHeight();
  const m = 18;
  const cw = pw - m * 2;
  let y = 0;

  // Load the Paper Boat logo
  const logoData = await loadImageAsBase64('/logo-pdf-paperboat.png');

  // ════════════════════════════════════════════════
  // HEADER — Logo left, info right
  // ════════════════════════════════════════════════
  // Background — rgba(218, 248, 250) = logo's actual background
  doc.setFillColor(218, 248, 250);
  doc.rect(0, 0, pw, 46, 'F');

  // Logo on the left
  if (logoData) {
    const logoW = 42;
    const logoH = 44;
    doc.addImage(logoData, 'PNG', m, 1, logoW, logoH);
  }

  // Right side info
  const rightX = pw - m;
  doc.setTextColor(50, 80, 78);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text(WEBSITE, rightX, 18, { align: 'right' });

  doc.setFontSize(6.5);
  doc.setTextColor(80, 110, 108);
  doc.text(`Compromiso de Confidencialidad — ${version || CURRENT_VERSION}`, rightX, 25, { align: 'right' });

  // Thin dark line separator
  doc.setFillColor(30, 30, 30);
  doc.rect(0, 46, pw, 0.6, 'F');

  y = 54;

  // ════════════════════════════════════════════════
  // DOCUMENT TITLE
  // ════════════════════════════════════════════════
  doc.setFillColor(248, 250, 252);
  doc.setDrawColor(30, 30, 30);
  doc.setLineWidth(0.6);
  doc.roundedRect(m, y, cw, 20, 2, 2, 'FD');

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('COMPROMISO DE CONFIDENCIALIDAD Y RESGUARDO DE INFORMACIÓN', pw / 2, y + 13, { align: 'center' });

  y += 28;

  // ════════════════════════════════════════════════
  // SIGNER INFO
  // ════════════════════════════════════════════════
  doc.setFillColor(240, 249, 255);
  doc.setDrawColor(186, 230, 253);
  doc.setLineWidth(0.3);
  doc.roundedRect(m, y, cw, 30, 3, 3, 'FD');

  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(3, 105, 161);
  doc.text('DATOS DEL FIRMANTE', m + 6, y + 8);

  const col1 = m + 6;
  const col2 = m + cw / 2 + 4;

  doc.setFontSize(8.5);
  doc.setTextColor(30, 30, 30);
  let iy = y + 16;

  doc.setFont('helvetica', 'bold');
  doc.text('Nombre:', col1, iy);
  doc.setFont('helvetica', 'normal');
  doc.text(userName || 'N/A', col1 + 26, iy);

  doc.setFont('helvetica', 'bold');
  doc.text('Email:', col2, iy);
  doc.setFont('helvetica', 'normal');
  const emailTxt = userEmail || 'N/A';
  doc.text(emailTxt.length > 28 ? emailTxt.substring(0, 28) + '...' : emailTxt, col2 + 20, iy);

  iy += 7;
  if (acceptedAt) {
    const dateStr = new Date(acceptedAt).toLocaleString('es-CL', { timeZone: 'America/Santiago' });
    doc.setFont('helvetica', 'bold');
    doc.text('Fecha:', col1, iy);
    doc.setFont('helvetica', 'normal');
    doc.text(dateStr, col1 + 22, iy);
  }
  if (ipAddress) {
    doc.setFont('helvetica', 'bold');
    doc.text('IP:', col2, iy);
    doc.setFont('helvetica', 'normal');
    doc.text(ipAddress, col2 + 12, iy);
  }

  y += 38;

  // ════════════════════════════════════════════════
  // LEGAL REFERENCES
  // ════════════════════════════════════════════════
  doc.setFillColor(245, 245, 248);
  doc.roundedRect(m, y, cw, 9, 2, 2, 'F');
  doc.setFontSize(6.5);
  doc.setTextColor(100, 100, 110);
  doc.setFont('helvetica', 'normal');
  doc.text('Ley N° 19.628 (Protección de Datos)   |   Ley N° 21.719 (Actualización 2024)   |   Ley N° 19.799 (Firma Electrónica)', pw / 2, y + 6, { align: 'center' });

  y += 15;

  // ════════════════════════════════════════════════
  // INTRO
  // ════════════════════════════════════════════════
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 55);

  const intro = `Yo, ${userName || '[usuario del sistema]'}, en mi calidad de usuario autorizado del sistema ${SOFTWARE_NAME}, declaro haber sido informado/a sobre la naturaleza confidencial de la información a la que tendré acceso, y me comprometo voluntariamente a cumplir las siguientes obligaciones:`;
  const introLines = doc.splitTextToSize(intro, cw);
  doc.text(introLines, m, y);
  y += introLines.length * 4.3 + 6;

  // ════════════════════════════════════════════════
  // COMMITMENT POINTS
  // ════════════════════════════════════════════════
  DOC_POINTS.forEach((point, i) => {
    if (y > ph - 55) {
      addFooter(doc, pw, ph, m);
      doc.addPage();
      y = 18;
    }

    // Number badge — rounded square
    doc.setFillColor(15, 15, 15);
    doc.roundedRect(m, y - 4, 10, 10, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.text(`${i + 1}`, m + 5, y + 1.5, { align: 'center' });

    // Text
    doc.setTextColor(50, 50, 55);
    doc.setFontSize(8.5);
    doc.setFont('helvetica', 'normal');
    const pointLines = doc.splitTextToSize(point, cw - 16);
    doc.text(pointLines, m + 14, y);
    y += pointLines.length * 4 + 7;
  });

  y += 2;

  // ════════════════════════════════════════════════
  // SEPARATOR
  // ════════════════════════════════════════════════
  doc.setDrawColor(210, 210, 215);
  doc.setLineWidth(0.3);
  doc.line(m, y, pw - m, y);
  y += 6;

  // ════════════════════════════════════════════════
  // PROVIDER / DATA RESPONSIBILITY CLAUSE
  // ════════════════════════════════════════════════
  if (y > ph - 70) {
    addFooter(doc, pw, ph, m);
    doc.addPage();
    y = 18;
  }

  doc.setFillColor(250, 250, 253);
  doc.setDrawColor(220, 220, 230);
  doc.setLineWidth(0.3);
  doc.roundedRect(m, y, cw, 38, 3, 3, 'FD');

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(50, 50, 60);
  doc.text('SOBRE EL TRATAMIENTO DE DATOS', m + 6, y + 8);

  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(70, 70, 80);

  const providerText = `El sistema ${SOFTWARE_NAME} es desarrollado y mantenido por ${PROVIDER_LEGAL_NAME} (${WEBSITE}), quien actúa en calidad de prestador de servicios tecnológicos conforme al Art. 8 de la Ley N° 19.628. ${PROVIDER_LEGAL_NAME} se compromete a resguardar la seguridad e integridad de los datos almacenados en el sistema, implementando las medidas técnicas y organizativas necesarias para prevenir el acceso no autorizado, la alteración o pérdida de información. Los datos ingresados al sistema son propiedad y responsabilidad de cada empresa usuaria.`;
  const provLines = doc.splitTextToSize(providerText, cw - 12);
  doc.text(provLines, m + 6, y + 14);

  y += 46;

  // ════════════════════════════════════════════════
  // LEGAL VALIDITY
  // ════════════════════════════════════════════════
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  doc.setTextColor(107, 114, 128);
  const legalNote = 'La aceptación electrónica de este documento tiene plena validez legal conforme a la Ley N° 19.799 sobre Documentos Electrónicos y Firma Electrónica. El registro incluye fecha, hora, dirección IP y agente del navegador como evidencia de la aceptación.';
  const legalLines = doc.splitTextToSize(legalNote, cw);
  doc.text(legalLines, m, y);
  y += legalLines.length * 3.6 + 8;

  // ════════════════════════════════════════════════
  // ACCEPTANCE STAMP
  // ════════════════════════════════════════════════
  if (y > ph - 50) {
    addFooter(doc, pw, ph, m);
    doc.addPage();
    y = 18;
  }

  const stampX = m + 15;
  const stampW = cw - 30;
  const stampH = 28;

  // Green box
  doc.setFillColor(236, 253, 245);
  doc.setDrawColor(34, 197, 94);
  doc.setLineWidth(1);
  doc.roundedRect(stampX, y, stampW, stampH, 4, 4, 'FD');

  // Inner border
  doc.setDrawColor(187, 247, 208);
  doc.setLineWidth(0.3);
  doc.roundedRect(stampX + 2.5, y + 2.5, stampW - 5, stampH - 5, 2.5, 2.5, 'S');

  // Green checkmark circle
  const cx = pw / 2 - 52;
  const cy = y + stampH / 2;
  doc.setFillColor(34, 197, 94);
  doc.circle(cx, cy, 6, 'F');

  // White check inside circle — draw thick V
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(1.8);
  doc.setLineCap('round');
  doc.setLineJoin('round');
  doc.line(cx - 3, cy, cx - 0.5, cy + 3);
  doc.line(cx - 0.5, cy + 3, cx + 4, cy - 3);

  // Text next to checkmark
  doc.setTextColor(22, 101, 52);
  doc.setFontSize(13);
  doc.setFont('helvetica', 'bold');
  doc.text('ACEPTADO ELECTRÓNICAMENTE', cx + 10, cy - 1);

  const stampDate = acceptedAt
    ? new Date(acceptedAt).toLocaleString('es-CL', { timeZone: 'America/Santiago' })
    : new Date().toLocaleString('es-CL', { timeZone: 'America/Santiago' });
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`${stampDate}   •   IP: ${ipAddress || 'N/A'}`, cx + 10, cy + 6);

  // Reset line settings
  doc.setLineCap('butt');
  doc.setLineJoin('miter');

  // ════════════════════════════════════════════════
  // FOOTER
  // ════════════════════════════════════════════════
  addFooter(doc, pw, ph, m);

  // Return base64 or save to file
  if (returnBase64) {
    return doc.output('datauristring').split(',')[1];
  }
  const fileName = `Compromiso_Confidencialidad_${version || CURRENT_VERSION}_${(userEmail || 'usuario').split('@')[0]}.pdf`;
  doc.save(fileName);
}

function addFooter(doc, pw, ph, m) {
  const fy = ph - 10;
  doc.setDrawColor(220, 220, 225);
  doc.setLineWidth(0.2);
  doc.line(m, fy - 4, pw - m, fy - 4);

  doc.setTextColor(170, 170, 175);
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`© ${new Date().getFullYear()} ${SOFTWARE_NAME} — Documento generado automáticamente — No requiere firma manuscrita`, pw / 2, fy, { align: 'center' });
}

export { CURRENT_VERSION, DOC_POINTS };
